import React from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, HandHeart } from 'lucide-react';
import { api } from '../api/client';
import {
  CalendarGrid, DayEventList, EventDetail, WallEventDetail, PrayerStatsCard,
  ymd, weekRange,
} from './PrayerScreen';
import { PrayingHandsIcon } from '../components/Icons';
import { useTranslation } from '../i18n';
import { FastingPrayerDetail } from './fasting/components/FastingPrayerCard';

function formatRangeLabel(viewDate, mode, MONTHS) {
  if (mode === 'month') {
    return `${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
  }
  const { start, end } = weekRange(viewDate);
  const sM = MONTHS[start.getMonth()].slice(0, 3);
  const eM = MONTHS[end.getMonth()].slice(0, 3);
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${sM} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  if (start.getFullYear() === end.getFullYear()) {
    return `${sM} ${start.getDate()} – ${eM} ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${sM} ${start.getDate()}, ${start.getFullYear()} – ${eM} ${end.getDate()}, ${end.getFullYear()}`;
}

function isTodayInRange(viewDate, mode) {
  const today = ymd(new Date());
  if (mode === 'month') {
    return viewDate.getFullYear() === new Date().getFullYear()
      && viewDate.getMonth() === new Date().getMonth();
  }
  const { start, end } = weekRange(viewDate);
  return ymd(start) <= today && today <= ymd(end);
}

export default function PrayerCalendarScreen() {
  const navigate = useNavigate();
  const { lang } = useTranslation();
  const {
    recipients, calEvents, wallCalEvents, setWallCalEvents, fastCalEvents, setFastCalEvents,
    viewDate, selectedDay, setSelectedDay, selectedEvent, setSelectedEvent,
    detailRef, MONTHS, unprayedToday, selectedDayEvts, selectedWallEvts, selectedFastEvts,
    viewMode, setViewMode, shiftRange, goToToday, openAdd,
    handleEventPrayed, handleNotesChange, handleEventUpdate,
    currentUserId, refreshDots, t,
    coverPhotos, prayerStats, refreshStats, totalToday,
    handleInvite,
  } = useOutletContext();

  // Open the first unprayed prayer for today from the hero card's "Pray now" button.
  // PrayerLayout clears selectedEvent whenever selectedDay changes, so defer the
  // selection a tick so it survives that reset effect when the day actually changes.
  function handlePrayNowFromCard() {
    const today = ymd(new Date());
    const recip = calEvents.find(e => e.scheduledFor.slice(0, 10) === today && !e.prayedAt);
    const wall  = wallCalEvents.find(e => e.scheduledFor.slice(0, 10) === today && !e.prayedAt);
    const fast  = fastCalEvents.find(e => e.scheduledFor.slice(0, 10) === today && !e.prayedAt);
    const next  = recip || (wall ? { ...wall, _isWall: true } : null) || (fast ? { ...fast, _isFast: true } : null);
    if (!next) return;
    setSelectedDay(today);
    setTimeout(() => setSelectedEvent(next), 0);
  }

  const rangeLabel = formatRangeLabel(viewDate, viewMode, MONTHS);
  const showToday  = !isTodayInRange(viewDate, viewMode);

  // Pray Now came out of the avatar menu, where nobody found it. It lives in
  // Prayer now — but as an action, not a tab: it is a verb, not a place.
  const prayNowButton = (
    <button className="cal-praynow-btn" onClick={() => navigate('/pray-now')}>
      <HandHeart size={20} strokeWidth={2} />
      <span>
        <span className="cal-praynow-label">{t('nav.prayNow')}</span>
        <span className="cal-praynow-sub">{t('nav.prayNowDesc')}</span>
      </span>
    </button>
  );

  // Nobody to pray for yet. Adding someone is the main thing to do, but Pray
  // Now works from the very first minute, so offer it here too.
  if (!recipients.length && !wallCalEvents.length && !fastCalEvents.length) {
    return (
      <div className="cal-wrap">
        <div className="cal-empty-state">
          <PrayingHandsIcon size={48} color="var(--teal)" strokeWidth={1.5} />
          <p style={{ fontWeight: 700, fontSize: 16, color: 'var(--teal)', marginBottom: 6 }}>{t('prayer.noPrayerRecipients')}</p>
          <p style={{ color: 'var(--text-lt)', marginBottom: 16 }}>{t('prayer.noRecipientsYet')}</p>
          <button className="btn btn-primary" onClick={openAdd}>+ {t('prayer.addFirstRecipient')}</button>
        </div>
        {prayNowButton}
      </div>
    );
  }

  return (
    <div className="cal-wrap">
      <PrayerStatsCard
        unprayedToday={unprayedToday}
        totalToday={totalToday}
        currentStreak={prayerStats?.currentStreak || 0}
        bestStreak={prayerStats?.bestStreak || 0}
        onPrayNow={handlePrayNowFromCard}
        t={t}
      />
      {prayNowButton}
      <div className="card cal-card">
        <div className="cal-card-header">
          <div className="cal-card-nav">
            <button className="cal-nav-btn" onClick={() => shiftRange(-1)} aria-label={t('common.back')}>
              <ChevronLeft size={18}/>
            </button>
            <span className="cal-month-label">{rangeLabel}</span>
            <button className="cal-nav-btn" onClick={() => shiftRange(1)} aria-label={t('common.next')}>
              <ChevronRight size={18}/>
            </button>
          </div>
          <div className="cal-card-actions">
            {showToday && (
              <button className="cal-today-btn" onClick={goToToday}>
                {t('prayer.todayBtn')}
              </button>
            )}
            <div className="cal-view-toggle" role="tablist" aria-label="View mode">
              <button
                role="tab"
                aria-selected={viewMode === 'week'}
                className={`cal-view-toggle-btn ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}>
                {t('prayer.viewWeek')}
              </button>
              <button
                role="tab"
                aria-selected={viewMode === 'month'}
                className={`cal-view-toggle-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}>
                {t('prayer.viewMonth')}
              </button>
            </div>
          </div>
        </div>
        <div className="cal-card-divider"/>
        <div className="cal-card-body">
          <CalendarGrid
            events={[...fastCalEvents.map(e => ({ ...e, _isFast: true })), ...calEvents, ...wallCalEvents.map(e => ({ ...e, _isWall: true }))]}
            viewDate={viewDate} selectedDay={selectedDay}
            onSelectDay={key => { setSelectedDay(key); setSelectedEvent(null); }}
            currentUserId={currentUserId}
            mode={viewMode}
          />
        </div>
      </div>
      <div className="card" style={{ padding: '14px' }}>
        <DayEventList dayKey={selectedDay} events={selectedDayEvts}
          wallEvents={selectedWallEvts}
          fastEvents={selectedFastEvts}
          currentUserId={currentUserId}
          selectedEventId={selectedEvent?.id} onSelectEvent={e => setSelectedEvent(e)}
          onWallEventPrayed={async (evt) => {
            await api.post(`/api/prayer-wall/calendar/${evt.id}/prayed`);
            setWallCalEvents(prev => prev.map(e => e.id === evt.id ? { ...e, prayedAt: new Date().toISOString() } : e));
            refreshStats?.();
          }}
        />
      </div>
      {selectedEvent && (
        <div ref={detailRef}>
          {selectedEvent._isFast
            ? <FastingPrayerDetail
                event={selectedEvent} t={t} lang={lang} MONTHS={MONTHS}
                onClose={() => setSelectedEvent(null)}
                onUpdate={(updated) => {
                  setFastCalEvents(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
                  refreshDots?.();
                }}
                onHidden={(programId) => {
                  setFastCalEvents(prev => prev.filter(e => e.programId !== programId));
                  setSelectedEvent(null);
                }}
              />
            : selectedEvent._isWall
            ? <WallEventDetail
                event={selectedEvent}
                currentUserId={currentUserId}
                coverPhotos={coverPhotos}
                onClose={() => setSelectedEvent(null)}
                onPrayed={(updated) => {
                  setWallCalEvents(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
                  setSelectedEvent(null);
                  refreshDots();
                }}
                onEventUpdate={(updated) => {
                  setWallCalEvents(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
                  setSelectedEvent(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
                }}
              />
            : <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)}
                coverPhotos={coverPhotos}
                onPrayed={handleEventPrayed} onNotesChange={handleNotesChange} onEventUpdate={handleEventUpdate}
                onInvite={handleInvite} />
          }
        </div>
      )}
    </div>
  );
}
